import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

import { CountryInfo } from 'src/app/interfaces/country-info.interface';
import { Attribute } from 'src/app/interfaces/form-and-field.interface';
import { PhoneNumberService } from 'src/app/services/phone-number.service';
import { MaybeNull } from 'src/app/utils';
import { DateValidationService } from './components/date-validation.service';

export class FieldCustomValidators {
  /**
   * @description Checks if the control value is not empty and doesn't contains whitespaces
   *              or the same character 3 times in a row.
   * @param {RegExp} regex The specified regex
   * @returns {ValidatorFn}
   */
  public static checkWhitespaceAndRepeatedCharacters(regex: RegExp): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return control.value && regex.test(control.value as string) ? { findPasswordRegEx: true } : null;
    };
  }

  /**
   * @description Checks if the control value is not empty and doesn't contains whitespaces
   *              or the same character 3 times in a row.
   * @param {RegExp} regex The specified regex
   * @param {ValidationErrors} error The specified regex
   * @returns {ValidatorFn}
   */
  public static patternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return !control.value || regex.test(control.value as string) ? null : error;
    };
  }

  /**
   * @description Checks if the password and the repeatPassword values are equal and not empty.
   *              In case fail returns validation error.
   * @param {AbstractControl} form Registration form
   * @returns {ValidationErrors | null}
   */
  public static passwordMatches(form: AbstractControl): ValidationErrors | null {
    const password: MaybeNull<AbstractControl> = form.get('password');
    const repeatPassword: MaybeNull<AbstractControl> = form.get('repeatPassword');

    if (password && repeatPassword) {
      if (password.value !== repeatPassword.value) {
        return { passwordsNotMatch: true };
      }
    }

    return null;
  }

  /**
   * @description Checks if the phone number is valid for selected country.
   *              In case fail returns validation error.
   * @param {CountryInfo} countryInfo Contains selected country info.
   * @param {PhoneNumberService} phoneNumberService PhoneNumberService instance.
   * @returns {ValidatorFn}
   */
  public static phoneValidator(countryInfo: CountryInfo, phoneNumberService: PhoneNumberService): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let isValidNumber = false;
      try {
        isValidNumber = phoneNumberService.isPhoneNumberValidForISO(control.value as string, countryInfo.iso);
      } catch (e) {
        // Simply ignore
      }

      return isValidNumber ? null : { phone: true };
    };
  }

  public static requiredIfSelectionEqualsTo(enableControlName: string, enabledValue: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.parent) {
        return null;
      }

      const enableControl: MaybeNull<AbstractControl> = control.parent.get(enableControlName);

      if (!enableControl) {
        return null;
      }

      return enableControl.value === enabledValue && !control.value ? { required: true } : null;
    };
  }

  public static dateRangeValidator(backendAttribute: Attribute): ValidatorFn {
    const dateValidators: DateValidationService = new DateValidationService();
    return dateValidators.dateRangeValidator(backendAttribute.name);
  }

  public static notExistingDateValidator(): ValidatorFn {
    const dateValidators: DateValidationService = new DateValidationService();
    return dateValidators.notExistingDateValidator();
  }

  public static yearLessThenAllowed(): ValidatorFn {
    const dateValidators: DateValidationService = new DateValidationService();
    return dateValidators.yearLessThenAllowed();
  }

  public static nonEmptyValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const isValid = control.value && (control.value as string).trim().length > 0;
      return isValid ? null : { required: true };
    };
  }
}
